import React, { Component, Fragment } from 'react';
import MaskedInput from 'react-text-mask';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from 'components/cosmos';
import Html from 'components/Html';
import { Select } from 'components/input/select';
import { Toggle } from 'components/toggle';
import { InfoTooltip } from 'components/tooltip';
import { I18n, translate } from 'components/translate';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { ContentPageService } from 'modules/content';
import { DrippingFormStore } from 'modules/content/dripping/stores';
import { QuizService } from 'modules/content/quiz';
import PropTypes from 'prop-types';

import './QuizSettingsBox.scss';
@translate()
@inject((stores) => ({
  QuizStore: stores.QuizStore,
  MembershipStore: stores.MembershipStore,
  hasExpiration: stores.DrippingStore.hasExpiration
}))
@observer
export default class QuizSettingsBox extends Component {
  static propTypes = {
    QuizStore: MobxPropTypes.objectOrObservableObject,
    MembershipStore: MobxPropTypes.objectOrObservableObject,
    currentTab: PropTypes.number
  };

  state = {
    certificates: []
  };

  constructor(props) {
    super(props);
    if (props.MembershipStore.academy) {
      this.loadCertificates();
    }
  }

  loadCertificates = async () => {
    const response = await ContentPageService.getCertificates();
    const certificates = [];

    response.forEach((item) => {
      certificates.push({ value: item.id, label: item.name });
    });
    this.setState({ certificates });
  };

  handleToggle(event) {
    const { name, value } = event.target;

    QuizService[name] = value;
  }

  handleMinimumScoreRequiredChange = (event) => {
    DrippingFormStore.setQuizRequiresMinimumScore(event.target.checked);
    this.handleToggle(event);
  };

  setMinimumScorePercent = (e, field) => {
    let { value } = e.target;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    value = parseInt(value) > 100 ? '100' : value;
    QuizService[field] = value.replace(/_/g, '');
  };

  setRetryInterval = (e) => {
    const { value } = e.target;

    QuizService.retryInterval = value ? value.replace(/_/g, '') : null;
  };

  setRandomQuestion = (e) => {
    QuizService.maxQuestionToRandom = e.value;
  };

  setCertificate = (certificate) => {
    QuizService.certificate = certificate.value;
  };

  get selectOptions() {
    return [
      { value: '5', label: '5' },
      { value: '10', label: '10' },
      { value: '15', label: '15' },
      { value: '20', label: '20' }
    ];
  }

  get certificateValue() {
    const { certificates } = this.state;
    const { configuration } = this.props.QuizStore;

    if (certificates.length && configuration.certificate) {
      return certificates.find((c) => c.value === configuration.certificate);
    }

    return null;
  }

  canBeRedoneQuestion(configuration) {
    return (
      <div className="quiz__setting">
        <label className="quiz__setting__label">
          <I18n label="quiz_editor.settings.can_be_redone" />
          {configuration.retry}
        </label>
        <Toggle
          id="retry"
          checked={configuration.retry}
          disabled={configuration.minimumScoreRequired}
          onChange={(event) => this.handleToggle(event)}
        />
      </div>
    );
  }

  retryIntervalQuestion(configuration, academy) {
    return (
      ((configuration.retry && academy) ||
        configuration.minimumScoreRequired) && (
        <div className="quiz__setting">
          <label className="quiz__setting__label">
            <I18n label="quiz_editor.settings.retry_interval" />
          </label>
          <div>
            <MaskedInput
              guide={false}
              mask={[/\d/, /\d/, /\d/, /\d/]}
              className="quiz__setting__input"
              value={configuration.retryInterval}
              onChange={this.setRetryInterval}
            />
          </div>
        </div>
      )
    );
  }

  randomQuestion(configuration) {
    return (
      <div className="quiz__setting">
        <label className="quiz__setting__label">
          <I18n label="quiz_editor.settings.random_questions" />
        </label>
        <Toggle
          id="randomQuestion"
          checked={configuration.randomQuestion}
          onChange={(event) => this.handleToggle(event)}
        />
      </div>
    );
  }

  maxQuestion(configuration) {
    return (
      configuration.randomQuestion && (
        <div className="quiz__setting">
          <label className="quiz__setting__label">
            <I18n label="quiz_editor.settings.max_questions" />
          </label>
          <div>
            <Select
              isClearable={false}
              value={{
                value: configuration.maxQuestionToRandom,
                label: configuration.maxQuestionToRandom
              }}
              options={this.selectOptions}
              onChange={this.setRandomQuestion}
            />
          </div>
        </div>
      )
    );
  }

  renderProfileSettings() {
    const { configuration } = this.props.QuizStore;
    const { academy } = this.props.MembershipStore;

    return (
      <Fragment>
        {this.canBeRedoneQuestion(configuration)}

        {this.retryIntervalQuestion(configuration, academy)}

        {this.randomQuestion(configuration)}

        {this.maxQuestion(configuration)}

        {academy && this.renderAcademySettings()}
      </Fragment>
    );
  }

  renderExpirationWarning() {
    const { hasExpiration, t, QuizStore } = this.props;
    const { configuration } = QuizStore;

    return (
      configuration.minimumScoreRequired &&
      hasExpiration && (
        <Alert
          type="warning"
          className="_d-flex _justify-content-between _gap-4 _text-yellow-darkest _my-4 _w-full"
        >
          <FontAwesomeIcon
            icon={['fal', 'exclamation-triangle']}
            className="_text-4 _mt-1"
          />
          <Html className="_m-0" tag="p">
            {t('quiz_editor.settings.expiration_warning')}
          </Html>
        </Alert>
      )
    );
  }

  renderExerciseSettings() {
    const { configuration } = this.props.QuizStore;
    const { academy } = this.props.MembershipStore;
    const { currentTab } = this.props;

    return (
      <Fragment>
        {!academy && (
          <div className="quiz__setting _flex-wrap">
            <label className="quiz__setting__label">
              <I18n label="quiz_editor.settings.require_minimum_score" />
              <InfoTooltip
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                rerenderPosition={currentTab === 2}
                text="quiz_editor.settings.minimum_score_tooltip"
              />
            </label>
            <Toggle
              id="minimumScoreRequired"
              checked={configuration.minimumScoreRequired}
              onChange={this.handleMinimumScoreRequiredChange}
            />
            {this.renderExpirationWarning()}
          </div>
        )}

        {configuration.minimumScoreRequired && (
          <div className="quiz__setting">
            <label className="quiz__setting__label">
              <I18n label="quiz_editor.settings.minimum_score_percent" />
            </label>
            <div>
              <MaskedInput
                guide={false}
                mask={[/\d/, /\d/, /\d/]}
                className="quiz__setting__input"
                value={configuration.minimumScorePercent}
                onChange={(e) =>
                  this.setMinimumScorePercent(e, 'minimumScorePercent')
                }
              />
            </div>
          </div>
        )}

        {academy && this.canBeRedoneQuestion(configuration)}

        {this.retryIntervalQuestion(configuration, academy)}

        {!academy && this.canBeRedoneQuestion(configuration)}

        <div className="quiz__setting">
          <label className="quiz__setting__label">
            <I18n label="quiz_editor.settings.show_answers" />
          </label>
          <Toggle
            id="showAnswersAfterResult"
            checked={
              configuration.minimumScoreRequired
                ? false
                : configuration.showAnswersAfterResult
            }
            onChange={(event) => this.handleToggle(event)}
            disabled={configuration.minimumScoreRequired}
          />
        </div>

        {this.randomQuestion(configuration)}

        {this.maxQuestion(configuration)}

        {academy && this.renderAcademySettings()}

        <div className="quiz__setting">
          <label className="quiz__setting__label">
            <I18n label="quiz_editor.settings.shuffle_answers" />
          </label>
          <Toggle
            id="randomAnswerOrder"
            checked={configuration.randomAnswerOrder}
            onChange={(event) => this.handleToggle(event)}
          />
        </div>
      </Fragment>
    );
  }

  renderAcademySettings() {
    const { configuration } = this.props.QuizStore;

    return (
      <Fragment>
        <div className="quiz__setting">
          <label className="quiz__setting__label">
            <I18n label="quiz_editor.settings.issue_certificate" />
          </label>
          <Toggle
            id="certificateActive"
            checked={configuration.certificateActive}
            onChange={(event) => this.handleToggle(event)}
          />
        </div>

        {configuration.certificateActive && [
          <div key="first" className="quiz__setting">
            <label className="quiz__setting__label">
              <I18n label="quiz_editor.settings.choose_certificate" />
            </label>
            <div>
              <Select
                className="certificate__select"
                value={this.certificateValue || this.state.certificates[0]}
                options={this.state.certificates}
                onChange={this.setCertificate}
              />
            </div>
          </div>,
          <div key="second" className="quiz__setting">
            <label className="quiz__setting__label">
              <I18n label="quiz_editor.settings.minimum_score_percent" />
            </label>
            <div>
              <MaskedInput
                guide={false}
                mask={[/\d/, /\d/, /\d/]}
                className="quiz__setting__input"
                value={configuration.certificateReleasePercentage}
                onChange={(e) =>
                  this.setMinimumScorePercent(e, 'certificateReleasePercentage')
                }
              />
            </div>
          </div>
        ]}
      </Fragment>
    );
  }

  render() {
    const { type } = this.props.QuizStore;
    const quizTypeExercise = 'EXERCISE';

    return (
      <div className="quiz__settings">
        {type === quizTypeExercise
          ? this.renderExerciseSettings()
          : this.renderProfileSettings()}
      </div>
    );
  }
}
